import type { CurriculumRelevance, EducationContext, Profile, Skill, StageId, SubjectId } from '@/core/types';
import { catalog } from '@/data/catalog';
import { CURRICULA, stageOrder } from '@/data/curriculum/structure';

/**
 * CURRICULUM ELIGIBILITY
 * Stage, pathway and track determine which skills are RELEVANT and how they
 * relate to the student's anchor (foundation / core / extension).
 * This module never reads or writes mastery — relevance is not evidence.
 */

export interface SubjectEligibility {
  subjectId: SubjectId;
  enrolled: boolean;
  anchorOrder: number;
  anchorLabel: string;
  track?: string;
  languageLevel?: string;
  curriculumIds: string[];
}

const LEVEL_ORDER: Record<string, number> = { A1: 1, A2: 2, B1: 3 };

const ROUTE: Record<string, string[]> = {
  T: ['T', 'R', 'S'],
  R: ['T', 'R'],
  S: ['T', 'S'],
  P: ['P'],
  PY: ['PY', 'P'],
};

export function defaultLanguageLevel(stage: StageId): string {
  const o = stageOrder(stage);
  if (o <= 9) return 'A1';
  if (o <= 11) return 'A2';
  return 'B1';
}

export function defaultMathTrack(education: EducationContext): string | undefined {
  const o = stageOrder(education.stage);
  if (o <= 10) return undefined;
  const choice = education.subjectChoices.math?.track;
  if (choice) return choice;
  if (education.pathway.startsWith('yrkesfag')) return 'PY';
  return 'T';
}

export function subjectEligibility(profile: Pick<Profile, 'education' | 'subjects'>, subjectId: SubjectId): SubjectEligibility {
  const subject = catalog.subject(subjectId)!;
  const edu = profile.education;
  const enrolled = profile.subjects.includes(subjectId);
  if (subject.stageModel.kind === 'language-level') {
    const lvl = edu.subjectChoices[subjectId]?.languageLevel ?? defaultLanguageLevel(edu.stage);
    return {
      subjectId, enrolled, anchorOrder: LEVEL_ORDER[lvl] ?? 1, anchorLabel: subject.stageModel.levels?.find((l) => l.id === lvl)?.label ?? lvl,
      languageLevel: lvl, curriculumIds: subject.curriculumIds,
    };
  }
  const track = subjectId === 'math' ? defaultMathTrack(edu) : undefined;
  const curriculumIds = CURRICULA.filter((c) => c.subjectId === subjectId && c.stages.includes(edu.stage) && (!c.track || c.track === track)).map((c) => c.id);
  return { subjectId, enrolled, anchorOrder: stageOrder(edu.stage), anchorLabel: edu.stage, track, curriculumIds };
}

/** Relevance of a skill to the student's current curriculum universe. */
export function skillRelevance(skill: Skill, elig: SubjectEligibility): CurriculumRelevance {
  if (!elig.enrolled) return 'unavailable';
  const subject = catalog.subject(skill.subjectId)!;
  if (subject.stageModel.kind === 'language-level') {
    const o = LEVEL_ORDER[skill.track ?? 'A1'] ?? 1;
    return o < elig.anchorOrder ? 'foundation' : o === elig.anchorOrder ? 'core' : 'extension';
  }
  const order = stageOrder(skill.stage);
  if (skill.track) {
    // Track-specific (upper secondary) skill
    if (elig.anchorOrder <= 10) return 'extension';
    const route = ROUTE[elig.track ?? 'T'] ?? [elig.track ?? 'T'];
    if (!route.includes(skill.track)) return 'other-track';
  }
  if (order < elig.anchorOrder) return 'foundation';
  if (order === elig.anchorOrder) return 'core';
  return 'extension';
}

export function stageDistance(skill: Skill, elig: SubjectEligibility): number {
  const subject = catalog.subject(skill.subjectId)!;
  if (subject.stageModel.kind === 'language-level') return (LEVEL_ORDER[skill.track ?? 'A1'] ?? 1) - elig.anchorOrder;
  return stageOrder(skill.stage) - elig.anchorOrder;
}

export type EligibilityMap = Record<string, SubjectEligibility>;

const skillOrderOf = (skill: Skill) => {
  const subject = catalog.subject(skill.subjectId)!;
  return subject.stageModel.kind === 'language-level' ? LEVEL_ORDER[skill.track ?? 'A1'] ?? 1 : stageOrder(skill.stage);
};

/**
 * The highest stage a domain reaches within the student's curriculum (not
 * above the anchor). Some domains stop before the anchor stage (e.g. a domain
 * whose last compulsory skill is in Grade 9) — readiness is measured against
 * what the curriculum expects in THAT domain.
 */
export function domainAnchor(domainId: string, elig: EligibilityMap): number {
  const skills = catalog.skillsInDomain(domainId);
  if (!skills.length) return 0;
  const e = elig[skills[0].subjectId];
  let best = -Infinity;
  for (const s of skills) {
    const r = skillRelevance(s, e);
    if (r === 'core' || r === 'foundation') best = Math.max(best, skillOrderOf(s));
  }
  return best === -Infinity ? e.anchorOrder : Math.min(best, e.anchorOrder);
}

/** Stage distance relative to the domain's own anchor. */
export function domainDistance(skill: Skill, elig: EligibilityMap): number {
  return skillOrderOf(skill) - domainAnchor(skill.domainId, elig);
}

export function computeEligibility(profile: Pick<Profile, 'education' | 'subjects'>): EligibilityMap {
  const out: EligibilityMap = {};
  for (const s of catalog.subjects) out[s.id] = subjectEligibility(profile, s.id);
  return out;
}

export function relevanceOf(skillId: string, elig: EligibilityMap): CurriculumRelevance {
  const skill = catalog.skill(skillId);
  if (!skill) return 'unavailable';
  return skillRelevance(skill, elig[skill.subjectId]);
}

/** Skills in the student's curriculum universe (core + foundation + extension on route). */
export function universe(elig: EligibilityMap, subjectId?: SubjectId): Skill[] {
  return catalog.skills.filter((s) => {
    if (subjectId && s.subjectId !== subjectId) return false;
    const r = skillRelevance(s, elig[s.subjectId]);
    return r === 'core' || r === 'foundation' || r === 'extension';
  });
}
